import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { equipmentsService } from '../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  PowerIcon,
} from '@heroicons/react/24/outline';

const EquipamentoForm = () => {
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    marca: '',
    modelo: '',
    numeroSerie: '',
    numeroCffa: '',
    dataCalibracao: '',
    dataProximaCalibracao: '',
    isActive: true,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadEquipments();
  }, []);

  const loadEquipments = async () => {
    try {
      setLoading(true);
      const data = await equipmentsService.getAll();
      setEquipments(data);
    } catch (error) {
      console.error('Erro ao carregar equipamentos:', error);
      toast.error('Erro ao carregar equipamentos');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // ✅ CAMPOS OBRIGATÓRIOS (apenas 3)
    if (!formData.marca.trim()) {
      newErrors.marca = 'Marca é obrigatória';
    }

    if (!formData.modelo.trim()) {
      newErrors.modelo = 'Modelo é obrigatório';
    }

    if (!formData.dataCalibracao) {
      newErrors.dataCalibracao = 'Data de calibração é obrigatória';
    } else {
      // ✅ VALIDAÇÃO: Data não pode ser futura (certificado emitido APÓS calibração)
      const calibrationDate = new Date(formData.dataCalibracao);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Zerar horas para comparar apenas data
      
      if (calibrationDate > today) {
        newErrors.dataCalibracao = 'Data de calibração não pode ser futura. Certificados são emitidos APÓS a calibração ser realizada.';
      } else {
        // ✅ VALIDAÇÃO: Calibração não pode ter mais de 1 ano (Portaria 19 item 3.5.d)
        const daysSinceCalibration = Math.floor((today - calibrationDate) / (1000 * 60 * 60 * 24));
        if (daysSinceCalibration > 365) {
          newErrors.dataCalibracao = `Calibração expirada há ${daysSinceCalibration - 365} dias. Portaria 19 exige calibração anual.`;
        }
      }
    }

    // ⚠️ VALIDAÇÕES OPCIONAIS (apenas se preenchidos)
    
    // Validar formato CFFA apenas se preenchido
    if (formData.numeroCffa.trim()) {
      const cffaRegex = /^\d{4}\/\d{4}\/[1-8]ª\sRegião\s\([A-Z, ]+\)$/;
      if (!cffaRegex.test(formData.numeroCffa)) {
        newErrors.numeroCffa = 'Formato inválido. Use: XXXX/YYYY/Nª Região (Estados)';
      }
    }

    // Validar próxima calibração apenas se preenchida
    if (formData.dataProximaCalibracao) {
      if (formData.dataCalibracao && formData.dataProximaCalibracao <= formData.dataCalibracao) {
        newErrors.dataProximaCalibracao = 'Próxima calibração deve ser posterior à data de calibração';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      setLoading(true);
      
      // Preparar dados com tratamento correto de datas opcionais
      const equipmentData = {
        marca: formData.marca,
        modelo: formData.modelo,
        numeroSerie: formData.numeroSerie || null,
        numeroCffa: formData.numeroCffa || null,
        dataCalibracao: new Date(formData.dataCalibracao).toISOString(),
        dataProximaCalibracao: formData.dataProximaCalibracao 
          ? new Date(formData.dataProximaCalibracao).toISOString() 
          : null,
        isActive: formData.isActive,
      };

      if (editingId) {
        await equipmentsService.update(editingId, equipmentData);
        toast.success('Equipamento atualizado com sucesso!');
      } else {
        await equipmentsService.create(equipmentData);
        toast.success('Equipamento cadastrado com sucesso!');
      }

      resetForm();
      loadEquipments();
    } catch (error) {
      console.error('Erro ao salvar equipamento:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar equipamento');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (equipment) => {
    setEditingId(equipment.id);
    setFormData({
      marca: equipment.marca,
      modelo: equipment.modelo,
      numeroSerie: equipment.numeroSerie,
      numeroCffa: equipment.numeroCffa,
      dataCalibracao: equipment.dataCalibracao.split('T')[0],
      dataProximaCalibracao: equipment.dataProximaCalibracao.split('T')[0],
      isActive: equipment.isAtivo,
    });
    setShowForm(true);
    setErrors({});
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      setLoading(true);
      await equipmentsService.toggleStatus(id);
      toast.success(currentStatus ? 'Equipamento desativado' : 'Equipamento ativado');
      loadEquipments();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status do equipamento');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este equipamento? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setLoading(true);
      await equipmentsService.delete(id);
      toast.success('Equipamento excluído com sucesso!');
      loadEquipments();
    } catch (error) {
      console.error('Erro ao excluir equipamento:', error);
      toast.error('Erro ao excluir equipamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      marca: '',
      modelo: '',
      numeroSerie: '',
      numeroCffa: '',
      dataCalibracao: '',
      dataProximaCalibracao: '',
      isActive: true,
    });
    setEditingId(null);
    setShowForm(false);
    setErrors({});
  };

  const isCalibrationExpired = (dataProximaCalibracao) => {
    return new Date(dataProximaCalibracao) < new Date();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Equipamentos</h2>
          <p className="text-gray-600 mt-1">Gerencie os equipamentos audiométricos (CFFA 2020)</p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Novo Equipamento
          </button>
        )}
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">
              {editingId ? 'Editar Equipamento' : 'Novo Equipamento'}
            </h3>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Marca */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Marca *
                </label>
                <input
                  type="text"
                  value={formData.marca}
                  onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.marca ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Interacoustics"
                />
                {errors.marca && (
                  <p className="text-red-500 text-sm mt-1">{errors.marca}</p>
                )}
              </div>

              {/* Modelo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Modelo *
                </label>
                <input
                  type="text"
                  value={formData.modelo}
                  onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.modelo ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: AD629"
                />
                {errors.modelo && (
                  <p className="text-red-500 text-sm mt-1">{errors.modelo}</p>
                )}
              </div>

              {/* Número de Série */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de Série
                </label>
                <input
                  type="text"
                  value={formData.numeroSerie}
                  onChange={(e) => setFormData({ ...formData, numeroSerie: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: 123456789 (opcional)"
                />
              </div>

              {/* Número CFFA */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número CFFA
                </label>
                <input
                  type="text"
                  value={formData.numeroCffa}
                  onChange={(e) => setFormData({ ...formData, numeroCffa: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.numeroCffa ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: 1234/2024/1ª Região (SP, MS, MT)"
                />
                {errors.numeroCffa && (
                  <p className="text-red-500 text-sm mt-1">{errors.numeroCffa}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Formato: XXXX/YYYY/Nª Região (Estados) - Opcional
                </p>
              </div>

              {/* Data de Calibração */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Calibração *
                </label>
                <input
                  type="date"
                  value={formData.dataCalibracao}
                  onChange={(e) => setFormData({ ...formData, dataCalibracao: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.dataCalibracao ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dataCalibracao && (
                  <p className="text-red-500 text-sm mt-1">{errors.dataCalibracao}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Portaria 19 item 3.5.d: calibração anual obrigatória
                </p>
              </div>

              {/* Data da Próxima Calibração */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Próxima Calibração
                </label>
                <input
                  type="date"
                  value={formData.dataProximaCalibracao}
                  onChange={(e) => setFormData({ ...formData, dataProximaCalibracao: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.dataProximaCalibracao ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dataProximaCalibracao && (
                  <p className="text-red-500 text-sm mt-1">{errors.dataProximaCalibracao}</p>
                )}
              </div>
            </div>

            {/* Status Ativo */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                Equipamento ativo
              </label>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {loading ? 'Salvando...' : editingId ? 'Atualizar' : 'Cadastrar'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Equipment List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading && !showForm ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2">Carregando equipamentos...</p>
          </div>
        ) : equipments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-lg">Nenhum equipamento cadastrado</p>
            <p className="text-sm mt-1">Clique em "Novo Equipamento" para adicionar</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Equipamento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Número CFFA
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Calibração
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {equipments.map((equipment) => {
                  const expired = isCalibrationExpired(equipment.dataProximaCalibracao);
                  
                  return (
                    <tr key={equipment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {equipment.marca} {equipment.modelo}
                        </div>
                        <div className="text-sm text-gray-500">
                          S/N: {equipment.numeroSerie}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {equipment.numeroCffa}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(equipment.dataCalibracao)}
                        </div>
                        <div className={`text-sm flex items-center gap-1 ${
                          expired ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {expired && <ExclamationTriangleIcon className="w-4 h-4" />}
                          Próxima: {formatDate(equipment.dataProximaCalibracao)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {equipment.isAtivo ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Ativo
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Inativo
                          </span>
                        )}
                        {expired && (
                          <div className="mt-1">
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Calibração vencida
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleToggleStatus(equipment.id, equipment.isAtivo)}
                            className={`p-1 ${
                              equipment.isAtivo 
                                ? 'text-green-600 hover:text-green-900' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title={equipment.isAtivo ? 'Desativar equipamento' : 'Ativar equipamento'}
                          >
                            <PowerIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleEdit(equipment)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Editar"
                          >
                            <PencilIcon className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(equipment.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Excluir permanentemente"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EquipamentoForm;
